import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import Drawer from "@via/components/drawer";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table from "@via/components/table/table.tsx";
import { useDeleteRunEnvironment } from "@via/pages/account/profile/profile.api.tsx";
import RunEnvironmentForm from "@via/pages/account/profile/run-environments/run-environment-form/run-environment-form.tsx";
import {
  AgentRowExpandedContent,
  isAgentRowExpandable,
} from "@via/pages/account/profile/run-environments/table/agent-row-expanded-content.tsx";
import { RUN_ENVIRONMENTS_TABLE } from "@via/utils/constants.tsx";
import { removeFalsyValues } from "@via/utils/helpers.tsx";
import type { TableContextMenuInterface } from "@via/utils/interfaces.tsx";
import { type ProfileClusterForClient } from "common";
import { JSX, useEffect, useRef, useState } from "react";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";

const columnHelper = createColumnHelper<ProfileClusterForClient>();

export default function RunEnvironmentsTable({
  runEnvironments,
  isPublic,
  focusEnvironmentId,
}: {
  runEnvironments: ProfileClusterForClient[];
  isPublic?: boolean;
  /** From the page's `?environment=<id>` (agent-4-2): open that agent environment's form at its Agent card, once. */
  focusEnvironmentId?: number | null;
}): JSX.Element {
  const [isFormDrawerOpen, setFormDrawerOpen] = useState(false);
  const [editableRunEnvironment, setEditableRunEnvironment] =
    useState<ProfileClusterForClient>();
  const [runEnvironmentToBeDeleted, setRunEnvironmentToBeDeleted] =
    useState<ProfileClusterForClient | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const {
    mutateAsync: deleteRunEnvironment,
    isPending: isDeleteRunEnvironmentLoading,
  } = useDeleteRunEnvironment();

  // agent-4-2 (spec A4). True only for a drawer a deep link opened; cleared by any ordinary Edit and when
  // the drawer closes, so the next Edit opens the form exactly as before.
  const [focusAgentCard, setFocusAgentCard] = useState(false);
  // One shot per page load: closing the drawer must not reopen it when the list refetches.
  const focusHandledRef = useRef(false);

  useEffect(() => {
    if (
      focusHandledRef.current ||
      undefined === focusEnvironmentId ||
      null === focusEnvironmentId
    ) {
      return;
    }
    const target = runEnvironments.find(
      environment => environment.id === focusEnvironmentId
    );
    // Not one of these rows, or not an agent environment (whose form has no Agent card): leave the list as it is.
    if (!target || !isAgentRowExpandable(target)) return;
    focusHandledRef.current = true;
    setFocusAgentCard(true);
    setEditableRunEnvironment(
      removeFalsyValues(target) as ProfileClusterForClient
    );
    setFormDrawerOpen(true);
  }, [focusEnvironmentId, runEnvironments]);

  const setDrawerOpen = (open: boolean): void => {
    setFormDrawerOpen(open);
    if (!open) setFocusAgentCard(false);
  };

  function handleEditAction(runEnvironment: ProfileClusterForClient) {
    setEditableRunEnvironment(
      removeFalsyValues(runEnvironment) as ProfileClusterForClient
    );
    setFocusAgentCard(false);
    setFormDrawerOpen(true);
  }

  function handleRemoveAction(runEnvironment: ProfileClusterForClient) {
    setRunEnvironmentToBeDeleted(runEnvironment);
  }

  function handleAction(
    option: TableContextMenuInterface,
    runEnvironment: ProfileClusterForClient
  ) {
    switch (option.name) {
      case RUN_ENVIRONMENTS_TABLE[0].name:
        handleEditAction(runEnvironment);
        break;
      case RUN_ENVIRONMENTS_TABLE[1].name:
        handleRemoveAction(runEnvironment);
        break;
      default:
        break;
    }
  }

  const toggleExpanded = (id: number): void => {
    setExpandedRows(previous => {
      const next = new Set(previous);
      const key = String(id);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const columns = [
    // ⚠️ This column is what makes the expanded row reachable at all. The shared Table renders
    // expanded content only for ids already in `expandedRows` and supplies no control of its
    // own, so passing `renderExpandedContent` without an expander here typechecks and can
    // never be opened. Only agent rows get one; ssh rows keep the drawer.
    ...(isPublic
      ? []
      : [
          columnHelper.display({
            id: "expand",
            header: () => <span className="sr-only">Expand</span>,
            cell: info => {
              const environment = info.row.original;
              if (!isAgentRowExpandable(environment)) return null;
              const isOpen = expandedRows.has(String(environment.id));
              return (
                <button
                  type="button"
                  aria-label={`${isOpen ? "Collapse" : "Expand"} ${environment.name}`}
                  aria-expanded={isOpen}
                  onClick={() => toggleExpanded(environment.id as number)}
                  className="px-1 text-gray-500 hover:text-gray-800">
                  {isOpen ? "▾" : "▸"}
                </button>
              );
            },
            enableSorting: false,
          }),
        ]),
    ...(isPublic
      ? []
      : [
          columnHelper.accessor("id", {
            header: () => <span>Id</span>,
            cell: info => {
              const value = info.getValue();
              if (value === null || value === undefined) return "-";
              return (
                <span className="font-mono text-xs text-gray-700">
                  {value.toString()}
                </span>
              );
            },
          }),
        ]),
    columnHelper.accessor("name", {
      header: () => <span>{isPublic ? "Profile Host" : "Profile Name"}</span>,
      cell: info => info.getValue(),
    }),
    columnHelper.accessor("containerType", {
      header: () => <span>Container Type</span>,
      cell: info => info.getValue(),
    }),
    columnHelper.accessor("nextPath", {
      header: () => <span>Details</span>,
      cell: info => {
        const { row } = info;
        const { nextPath, executor, username, hostname } = row.original;
        return (
          <div>
            <div>Workflow Directory Path: {nextPath}</div>
            <div>Executor: {executor}</div>
            <div>
              Connection: {username}@{hostname}
            </div>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "options",
      header: () => "Options",
      cell: info => (
        <TableContextMenu
          options={RUN_ENVIRONMENTS_TABLE}
          onSelect={option => {
            handleAction(option, info.row.original);
          }}
        />
      ),
      enableSorting: false,
    }),
  ] as Array<ColumnDef<ProfileClusterForClient>>;

  return (
    <div>
      <Table
        columns={columns}
        data={runEnvironments || []}
        total={runEnvironments?.length}
        // ⚠️ String ids, because the shared Table tracks expansion in a Set<string>. A public
        // template row has no owner-scoped id worth expanding, and isAgentRowExpandable keeps
        // it closed anyway by returning null below.
        getRowId={row => String(row.id)}
        expandedRows={expandedRows}
        onExpandedRowsChange={setExpandedRows}
        renderExpandedContent={row =>
          isAgentRowExpandable(row) ? (
            <AgentRowExpandedContent environment={row} />
          ) : null
        }
      />

      <Drawer
        id="editable-run-environment"
        open={isFormDrawerOpen}
        setOpen={setDrawerOpen}>
        {/*
          isPublic has to travel with the row. Public templates are created
          through the drawer on the run environments page and edited through
          this one, so leaving it out here made the drawer treat a template as
          a private environment: the ssh picker appeared for a row that has no
          owner, and the required-key rule then blocked the save until an admin
          attached one of their own keys to a shared row.
        */}
        <RunEnvironmentForm
          setOpen={setDrawerOpen}
          editableRunEnvironment={editableRunEnvironment}
          isPublic={isPublic}
          focusAgentCard={focusAgentCard}
        />
      </Drawer>

      <DeleteModal
        item={runEnvironmentToBeDeleted}
        setItem={setRunEnvironmentToBeDeleted}
        deleteFunction={() =>
          deleteRunEnvironment(runEnvironmentToBeDeleted!.id.toString())
        }
        isLoading={isDeleteRunEnvironmentLoading}
        title="Delete Run Environment"
      />
    </div>
  );
}
